// Knowledge.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Trash2, Search, Database, ArrowLeft } from 'react-feather';
import { API_BASE_URL } from '../config/api';
import PageContainer from '../components/PageContainer';
import Modal from '../components/Modal';
import '../styles/knowledge.css';
import Chunkssection from "./chunks_section";

// 1. Updated default config to include 'provider'
const defaultConfig = {
  name: '',
  knowledge_provider: 'azure_search',  // ← correct key
  dataset_id: '',
  score: 0.1,
  search_type: 'hybrid',
  page_size: 10,
  get_total_pages: false,
  slim_chunks: false,
  knowledge_embedding: 'text-embedding-3-small',
};
// The provider can arrive either nested under `config` or flat on the record,
// depending on which endpoint returned it. Read it in one place so the list
// badge and the details panel can never disagree about a KB's type, they
// previously used different fallback chains.
const getProvider = (kb) =>
  kb?.config?.knowledge_provider ?? kb?.knowledge_provider ?? 'azure_search';

const PROVIDER_LABELS = {
  azure_search: 'Advanced',
  lightrag: 'Core',
};

const getProviderLabel = (kb) => {
  const provider = getProvider(kb);
  return PROVIDER_LABELS[provider] || provider;
};

const getLightragUrl = (kb) => {
  // Construct the tenant ID: {user_id}-{kb_id}
  const datasetId = kb?.config?.dataset_id ?? kb?.id;
  const tenantId = `${kb.user_id}-${datasetId}`; 
  
  // Base URL of your hosted LightRAG instance
  const baseUrl = `${(process.env.REACT_APP_API_LIGHT_RAG_URL || '').replace(/\/+$/, '')}/webui/`;
  return `${baseUrl}?workspace=${encodeURIComponent(tenantId)}`;
};

const Knowledge = () => {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);
  const [config, setConfig] = useState(defaultConfig);
  const [crawlUrlsText, setCrawlUrlsText] = useState(''); // one URL per line

  const [selectedKB, setSelectedKB] = useState(null);
  const [deletingKBId, setDeletingKBId] = useState(null);
  const [query, setQuery] = useState('');
  const detailsRef = useRef(null);
  const listRef = useRef(null);

  // Filter by name or provider label so "core" / "advanced" both work as
  // search terms, not just the KB's own name.
  const visibleKBs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return knowledgeBases;
    return knowledgeBases.filter((kb) => {
      const name = (kb.name || `KB #${kb.id}`).toLowerCase();
      return name.includes(q) || getProviderLabel(kb).toLowerCase().includes(q);
    });
  }, [knowledgeBases, query]);

  // ---------- Helpers ----------

  const getAuthHeader = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const showTransientMessage = (setter, msg) => {
    setter(msg);
    setTimeout(() => setter(null), 3000);
  };

  // ---------- Fetch KBs ----------

  const fetchKnowledgeBases = async () => {
    try {
      setLoading(true);
      setListError(null);
      const response = await fetch(`${API_BASE_URL}/api/knowledge`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch knowledge bases');
      }
      const data = await response.json();
      setKnowledgeBases(data);
      // If selection disappeared, reset selectedKB
      if (selectedKB) {
        const stillExists = data.find((kb) => kb.id === selectedKB.id);
        if (!stillExists) {
          setSelectedKB(null);
        }
      }
    } catch (err) {
      setListError(err.message || 'Failed to load knowledge bases.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKnowledgeBases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- KB Creation ----------

  const handleOpenCreate = () => {
    setConfig(defaultConfig);
    setCrawlUrlsText('');
    setCreateError(null);
    setShowAddForm(true);
  };

  const handleCancelCreate = () => {
    setShowAddForm(false);
    setCreateError(null);
    setCreateSuccess(null);
  };

  const handleConfigChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (creating) return;
    try {
      setCreating(true);
      setCreateError(null);

      const crawl_urls = crawlUrlsText
        .split('\n')
        .map((u) => u.trim())
        .filter((u) => u.length > 0);

        const page_size = Number(config.page_size);

        if (!Number.isFinite(page_size) || page_size < 1) {
          setCreateError('Page size must be between 1 and 10.');
          return;
        }
        
      const payload = {
        ...config, // This now includes 'provider'
        score: Number(config.score),
        page_size: Number(config.page_size),
        dataset_id: config.dataset_id || null,
        crawl_urls,
      };

      const response = await fetch(`${API_BASE_URL}/api/knowledge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create knowledge base');
      }

      // Add new KB to list and select it
      setKnowledgeBases((prev) => [...prev, data]);
      setSelectedKB(data);
      showTransientMessage(setCreateSuccess, 'Knowledge base created');
      setShowAddForm(false);
    } catch (err) {
      setCreateError(err.message || 'Failed to create knowledge base.');
    } finally {
      setCreating(false);
    }
  };

  // ---------- Select KB & Files ----------

  const handleSelectKB = (kb) => {
    setSelectedKB(kb);

    // On mobile the two panes stack, so the details land below the whole
    // list, selecting a KB appeared to do nothing until you scrolled down
    // past every row. Bring the panel into view instead.
    if (window.matchMedia('(max-width: 900px)').matches) {
      // Wait for the panel to render before measuring where to scroll.
      requestAnimationFrame(() => {
        detailsRef.current?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
          block: 'start',
        });
      });
    }
  };

  const handleKBConfigSave = (updatedFields) => {
    setSelectedKB((prev) => ({
      ...prev,
      config: { ...prev.config, ...updatedFields },
    }));
    setKnowledgeBases((prev) =>
      prev.map((kb) =>
        kb.id === selectedKB.id
          ? { ...kb, config: { ...kb.config, ...updatedFields } }
          : kb
      )
    );
  };

  // ---------- Delete KB ----------

  const handleDeleteKB = async (kb) => {
    if (!window.confirm(`Delete knowledge base "${kb.name || kb.id}"? This cannot be undone.`)) {
      return;
    }
    try {
      setDeletingKBId(kb.id);
      const response = await fetch(`${API_BASE_URL}/api/knowledge/${kb.id}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeader(),
        },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to delete knowledge base');
      }
      // Remove from list
      setKnowledgeBases((prev) => prev.filter((item) => item.id !== kb.id));
      if (selectedKB && selectedKB.id === kb.id) {
        setSelectedKB(null);
      }
    } catch (err) {
      showTransientMessage(setListError, err.message || 'Failed to delete knowledge base.');
    } finally {
      setDeletingKBId(null);
    }
  };

  return (
    <PageContainer
      loading={loading}
      loadingMessage="Loading knowledge bases..."
      title="Knowledge"
      subtitle="Create and manage your knowledge bases"
    >
      {/* Global list messages */}
      {!showAddForm && listError && (
        <div className="save-message error">{listError}</div>
      )}

      {/* Floating create action, matching the Tools page. */}
      <button
        type="button"
        className="add-knowledge-button"
        onClick={handleOpenCreate}
        aria-label="Create knowledge base"
        title="Create knowledge base"
      >
        <Plus size={24} aria-hidden="true" />
      </button>

      {/* Create dialog. Uses the shared Modal so Esc-to-close, background
          scroll lock, backdrop click and the dialog ARIA roles all come for
          free, the previous hand-rolled backdrop had none of them. */}
      <Modal
        isOpen={showAddForm}
        onClose={handleCancelCreate}
        title="Create knowledge base"
      >
        <div className="knowledge-form-card">
              {createError && <div className="save-message error">{createError}</div>}
              {createSuccess && (
                <div className="save-message success">{createSuccess}</div>
              )}

              <form onSubmit={handleCreateSubmit}>
                <div className="grid-container">
                  {/* Name */}
                  <div className="input-group full-width">
                    <label htmlFor="kb-name">Name</label>
                    <input
                      id="kb-name"
                      type="text"
                      value={config.name}
                      onChange={(e) => handleConfigChange('name', e.target.value)}
                      placeholder="My knowledge base"
                      required
                    />
                  </div>

                  {/* Provider selection */}
                  <div className="input-group full-width">
                    <label htmlFor="kb-provider">Knowledge type</label>
                    <select
                      id="kb-provider"
                      value={config.knowledge_provider}
                      onChange={(e) => handleConfigChange('knowledge_provider', e.target.value)}
                      aria-describedby="kb-provider-hint"
                    >
                      <option value="azure_search">Advanced</option>
                      <option value="lightrag">Core</option>
                    </select>
                    <p className="input-hint" id="kb-provider-hint">
                      Advanced uses vector search over chunks. Core builds a
                      knowledge graph and is better for connected facts.
                    </p>
                  </div>

                  {/* Score */}
                  <div className="input-group">
                    <label htmlFor="kb-score">Score threshold</label>
                    <input
                      id="kb-score"
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={config.score}
                      onChange={(e) => handleConfigChange('score', e.target.value)}
                      aria-describedby="kb-score-hint"
                    />
                    <p className="input-hint" id="kb-score-hint">
                      Minimum relevance, 0–1. Higher returns fewer, closer matches.
                    </p>
                  </div>

                  {/* Page size */}
                  <div className="input-group">
                    <label htmlFor="kb-page-size">Page size</label>
                    <input
                      id="kb-page-size"
                      type="number"
                      min="1"
                      max="10"
                      value={config.page_size}
                      onChange={(e) =>
                        handleConfigChange('page_size', e.target.value)
                      }
                      aria-describedby="kb-page-size-hint"
                    />
                    <p className="input-hint" id="kb-page-size-hint">
                      Results returned per query, 1–10.
                    </p>
                  </div>

                  {/* Search Type */}
                  <div className="input-group">
                    <label htmlFor="kb-search-type">Search type</label>
                    <select
                      id="kb-search-type"
                      value={config.search_type}
                      onChange={(e) =>
                        handleConfigChange('search_type', e.target.value)
                      }
                    >
                      <option value="hybrid">Hybrid</option>
                      <option value="semantic">Semantic</option>
                      <option value="keyword">Keyword</option>
                    </select>
                  </div>

                  {/* Embedding */}
                  <div className="input-group">
                    <label htmlFor="kb-embedding">Embedding model</label>
                    <select
                      id="kb-embedding"
                      value={config.knowledge_embedding}
                      onChange={(e) =>
                        handleConfigChange('knowledge_embedding', e.target.value)
                      }
                    >
                      <option value="text-embedding-3-small">
                        text-embedding-3-small
                      </option>
                      <option value="text-embedding-3-large">
                        text-embedding-3-large
                      </option>
                    </select>
                  </div>

                  {/* Crawl URLs */}
                  <div className="input-group full-width">
                    <label htmlFor="kb-crawl-urls">Website URLs to crawl</label>
                    <textarea
                      id="kb-crawl-urls"
                      rows={4}
                      value={crawlUrlsText}
                      onChange={(e) => setCrawlUrlsText(e.target.value)}
                      placeholder={
                        'https://example.com/docs\nhttps://another-site.com/help'
                      }
                      aria-describedby="kb-crawl-urls-hint"
                    />
                    <p className="input-hint" id="kb-crawl-urls-hint">
                      One URL per line. Optional, you can add documents later.
                    </p>
                  </div>
                </div>

                <div className="button-container">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={handleCancelCreate}
                    disabled={creating}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`primary-button ${creating ? 'is-loading' : ''}`}
                    disabled={creating}
                  >
                    {creating ? 'Creating…' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
      </Modal>

      {/* List + Details layout */}
      <div className="knowledge-layout">
        {/* Left: list of KBs */}
        <div className="knowledge-list" ref={listRef}>
          <div className="knowledge-list-head">
            <h3>Your knowledge bases</h3>
            {knowledgeBases.length > 0 && (
              <span className="kb-count">{knowledgeBases.length}</span>
            )}
          </div>

          {/* Search appears only once the list is long enough to need it. */}
          {knowledgeBases.length > 4 && (
            <div className="kb-search">
              <Search size={15} aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search knowledge bases"
                aria-label="Search knowledge bases"
              />
            </div>
          )}

          {knowledgeBases.length === 0 && !loading && (
            <div className="empty-state">
              <div className="empty-state-icon">
                <Database size={20} aria-hidden="true" />
              </div>
              <p>No knowledge bases yet. Create one to get started.</p>
            </div>
          )}

          {knowledgeBases.length > 0 && visibleKBs.length === 0 && (
            <p className="kb-no-results">No knowledge bases match “{query}”.</p>
          )}

          {visibleKBs.map((kb) => {
            const isSelected = selectedKB && selectedKB.id === kb.id;
            const isDeleting = deletingKBId === kb.id;
            const provider = getProvider(kb);

            return (
              // A button so the row is keyboard-reachable and announced as
              // actionable; the delete control stops propagation so it does
              // not also select the row.
              <button
                type="button"
                key={kb.id}
                className={`knowledge-list-item${isSelected ? ' selected' : ''}`}
                onClick={() => handleSelectKB(kb)}
                aria-current={isSelected ? 'true' : undefined}
              >
                <div className="knowledge-list-item-main">
                  <div className="kb-name">{kb.name || `KB #${kb.id}`}</div>
                  {/* The raw database ID was dropped, it duplicated nothing
                      the user can act on. The provider is the one piece of
                      metadata that changes how the KB behaves. */}
                  <span className={`kb-badge kb-badge--${provider === 'lightrag' ? 'core' : 'advanced'}`}>
                    {getProviderLabel(kb)}
                  </span>
                </div>
                <span
                  className="icon-button delete-kb"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteKB(kb);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteKB(kb);
                    }
                  }}
                  aria-disabled={isDeleting}
                  aria-label={`Delete ${kb.name || `KB #${kb.id}`}`}
                  title="Delete knowledge base"
                >
                  <Trash2 size={16} />
                </span>
              </button>
            );
          })}
        </div>

        {/* Right: details */}
        <div className="knowledge-details" ref={detailsRef}>
          {/* Mobile-only: the list is a full screen above once the panes
              stack, so give the user a way back without scrolling. */}
          {selectedKB && (
            <button
              type="button"
              className="kb-back-to-list"
              onClick={() =>
                listRef.current?.scrollIntoView({
                  behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                    ? 'auto'
                    : 'smooth',
                  block: 'start',
                })
              }
            >
              <ArrowLeft size={15} aria-hidden="true" />
              All knowledge bases
            </button>
          )}

          {selectedKB ? (
            // Was `config?.knowledge_provider === 'lightrag'` compared against
            // itself on both sides of the ||, so the flat `knowledge_provider`
            // fallback the list already handled was never actually checked here.
            getProvider(selectedKB) === 'lightrag' ? (
              <div className="lightrag-wrapper">
                <iframe
                  src={getLightragUrl(selectedKB)}
                  title="LightRAG Interface"
                  allow="clipboard-write" // Allow copy-paste inside iframe
                  // The embedded app lays itself out against the iframe's
                  // width, not the phone's, so it only gets its mobile layout
                  // if this element is actually narrow, hence the full-bleed
                  // rule in knowledge.css.
                  loading="lazy"
                />
              </div>
            ) : (
              <Chunkssection
                knowledge_id={selectedKB.id}
                user_id={selectedKB.user_id}
                kb_name={selectedKB.name}
                tool_id={selectedKB.tool_id}
                knowledge_config={selectedKB.config}
                onSave={handleKBConfigSave}
              />
            )
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">
                <Database size={20} aria-hidden="true" />
              </div>
              <h3 className="empty-state-title">No knowledge base selected</h3>
              <p>Pick one from the list to manage its documents, chunks and
                retrieval settings.</p>
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default Knowledge;
